// src/components/UpdateModal.tsx
//
// iSentinel360 v0.4.5r2 — update confirmation modal with localized
// Play Protect warning preview.
//
// This component is "controlled" by UpdatePrompt: the parent calls
// useApkUpdate() ONCE and passes the hook result as props. This way
// the modal shares the same state as the banner (no double-hook
// problem — calling useApkUpdate twice would create two independent
// state instances and the install action from the modal wouldn't
// reflect in the banner's state).
//
// Two-step flow:
//   Step 1 (info):     "What's new" + release notes + Continue/Cancel
//   Step 2 (warning):  Screenshot of the Play Protect warning that
//                      Android will show, with step-by-step instructions
//                      ("Tap Install anyway, then Got it"), then the
//                      actual "Download and Update" button.
//
// v0.4.5r2 change: the Play Protect warning image is now selected
// based on the active i18n language (via usePlayProtectImage hook).
// Previously it was a single static English screenshot. Required
// assets: src/assets/play-protect-warning-{lang}.png — see the
// usePlayProtectImage hook header for the full file list.
//
// v0.4.5r2 change: every user-facing string is now a translation
// key (namespace `update.*`). The "Install anyway" / "Got it" /
// "Open" button labels in the step-by-step instructions are quoted
// in the translation strings — the translator should match Android's
// own UI translation for their language for consistency.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, ExternalLink, AlertTriangle } from 'lucide-react';
import { ApkUpdateInfo, InstallResult } from '../hooks/useApkUpdate';
import { usePlayProtectImage } from '../hooks/usePlayProtectImage';

interface Props {
  update: ApkUpdateInfo;
  onClose: () => void;
  install: (url: string) => Promise<InstallResult>;
  openSettings: () => void;
  installing: boolean;
  installError: string | null;
}

export function UpdateModal({
  update,
  onClose,
  install,
  openSettings,
  installing,
  installError,
}: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'info' | 'warning'>('info');
  // v0.4.5r2: localized Play Protect warning image. Picks the right
  // screenshot based on i18n.language; falls back to English.
  const playProtectWarning = usePlayProtectImage();

  const handleInstall = async () => {
    // The parent (UpdatePrompt) owns the install state via useApkUpdate.
    // We just call the function; the parent updates installError /
    // installing as appropriate. The modal re-renders based on the
    // props.
    await install(update.downloadUrl);
    // We deliberately do NOT call onClose() here on success — the
    // system installer takes over the screen, and the user can come
    // back to the app afterwards. Closing the modal on success would
    // hide the "Downloading..." feedback prematurely.
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget && !installing) onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {step === 'info' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-2">
              {t('update.modalTitle', { version: update.versionName })}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('update.currentVersion', { version: update.currentVersionName })}
            </p>

            {update.mandatory && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <strong>{t('update.mandatoryTitle')}</strong>{' '}
                  {t('update.mandatoryBody')}
                </div>
              </div>
            )}

            {update.releaseNotes && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('update.whatsNew')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                  {update.releaseNotes}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                {t('update.cancel')}
              </button>
              <button
                onClick={() => setStep('warning')}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {t('update.continue')}
              </button>
            </div>
          </div>
        )}

        {step === 'warning' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-2">{t('update.beforeInstall')}</h2>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
              {t('update.warningIntro')}
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
              {t('update.youWillSee')}
            </p>

            <div className="mb-3 border rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800">
              <img
                src={playProtectWarning}
                alt={t('update.playProtectAlt')}
                className="w-full h-auto"
              />
            </div>

            <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-1.5 mb-4 list-decimal list-inside">
              <li>{t('update.step1')}</li>
              <li>{t('update.step2')}</li>
              <li>{t('update.step3')}</li>
              <li>{t('update.step4')}</li>
            </ol>

            {installError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                {installError.includes('permission') ? (
                  <>
                    {t('update.permissionBody')}
                    <button
                      onClick={openSettings}
                      className="ml-2 inline-flex items-center gap-1 text-red-700 underline"
                    >
                      {t('update.openSettings')} <ExternalLink className="h-3 w-3" />
                    </button>
                  </>
                ) : (
                  <>
                    {t('update.errorFormat', { error: installError })}
                  </>
                )}
              </div>
            )}

            <div className="flex justify-between gap-2 mt-6">
              <button
                onClick={() => setStep('info')}
                disabled={installing}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded disabled:opacity-50"
              >
                {t('update.back')}
              </button>
              <button
                onClick={handleInstall}
                disabled={installing}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {installing ? t('update.downloading') : t('update.downloadAndUpdate')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
